import { env } from '../../utils/env';

type MoveCategory = 'physical' | 'special' | 'status';
type Type =
    | 'normal'
    | 'fire'
    | 'water'
    | 'electric'
    | 'grass'
    | 'ice'
    | 'fighting'
    | 'poison'
    | 'ground'
    | 'flying'
    | 'psychic'
    | 'bug'
    | 'rock'
    | 'ghost'
    | 'dragon'
    | 'dark'
    | 'steel'
    | 'fairy';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ApiMoveResponse = any;

export interface Move {
    // Basic identification
    id: number;
    identifier: string;
    name: string;
    effect: string | null;

    // Move properties
    type: Type;
    category: MoveCategory;
    power: number | null; // null since status moves don't have power
    accuracy: number | null; // null since some moves always hit (-1) or have no accuracy
    one_hit_ko: boolean;
    maxPP: number;
    effect_probability: number; // deprecated, kept for backward compatibility
    user_effect_probability: number | null;
    target_effect_probability: number | null;
    critical_hit_stage: number;

    // Status effects
    effect_volatile_status_user: string | null;
    effect_nonvolatile_status_user: string | null;
    effect_volatile_status_foe: string | null;
    effect_nonvolatile_status_foe: string | null;

    // Stat modifications (using numbers for stages: -6 to +6)
    effect_attack_user: number;
    effect_defense_user: number;
    effect_special_attack_user: number;
    effect_special_defense_user: number;
    effect_speed_user: number;
    effect_critical_hit_ratio_user: number;

    effect_attack_foe: number;
    effect_defense_foe: number;
    effect_special_attack_foe: number;
    effect_special_defense_foe: number;
    effect_speed_foe: number;
    effect_accuracy_user: number;
    effect_evasiveness_user: number;
    effect_accuracy_foe: number;
    effect_evasiveness_foe: number;

    // Move mechanics
    recoil_percentage: number;
    recovery_percentage: number;
    min_hits: number;
    max_hits: number;
    priority: number;
    entry_hazard: string | null;
    target: string; // Could be made more specific with union types if needed

    // Move flags
    contact: boolean;
    custom_implementation: boolean;
    implemented: boolean;
    double_damage_if_hit: boolean;
    double_damage_if_no_item: boolean;

    // Metadata
    createdAt: number; // Unix timestamp
    updatedAt: number; // Unix timestamp
}

interface MoveManagerStorage {
    store: Record<string, Move>;
}

export default class MoveManager implements MoveManagerStorage {
    store: Record<string, Move>;
    useAPI: boolean; // Flag to toggle between API and CSV methods (defaults to API)

    constructor() {
        this.store = {};
        this.useAPI = true; // Default to API method
    }

    async loadAllMoves(): Promise<void> {
        if (this.useAPI) {
            await this.loadMovesFromAPI();
        } else {
            await this.loadMovesFromCSV();
        }
    }

    async loadMovesFromAPI(): Promise<void> {
        // Use VITE_API_BASE env var, defaulting to production API
        // For local dev, set VITE_API_BASE=http://localhost:4008/api
        const API_BASE: string =
            (env as { VITE_API_BASE: string }).VITE_API_BASE || 'https://api.pkmng.orly.io/api';

        try {
            // Django API game endpoint: /api/moves/game/
            // Returns all moves with full fields in snake_case format
            const response = await fetch(`${API_BASE}/moves/game/`);
            if (!response.ok) {
                throw new Error(`Failed to fetch moves: ${String(response.status)}`);
            }

            // Django REST Framework returns an array directly
            const movesArray = (await response.json()) as ApiMoveResponse[];

            if (Array.isArray(movesArray)) {
                movesArray.forEach((apiMove: ApiMoveResponse) => {
                    // Map API response to Move interface - using any type for API response

                    // Map API response to Move interface following the API format
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                    const name = apiMove.name;
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                    const identifier = apiMove.identifier;
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                    const effect = apiMove.effect;
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                    const type = apiMove.type as Type;
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                    const category = apiMove.category as MoveCategory;
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                    const power = apiMove.power;
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                    const accuracy = apiMove.accuracy;
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                    const one_hit_ko = apiMove.one_hit_ko;
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                    const maxPP = apiMove.max_pp ?? apiMove.maxPP;
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                    const effect_probability = apiMove.effect_probability;
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                    const user_effect_probability = apiMove.user_effect_probability;
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                    const target_effect_probability = apiMove.target_effect_probability;
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                    const critical_hit_stage = apiMove.critical_hit_stage;
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                    const effect_volatile_status_user = apiMove.effect_volatile_status_user;
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                    const effect_nonvolatile_status_user = apiMove.effect_nonvolatile_status_user;
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                    const effect_attack_user = apiMove.effect_attack_user;
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                    const effect_defense_user = apiMove.effect_defense_user;
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                    const effect_special_attack_user = apiMove.effect_special_attack_user;
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                    const effect_special_defense_user = apiMove.effect_special_defense_user;
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                    const effect_speed_user = apiMove.effect_speed_user;
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                    const effect_critical_hit_ratio_user = apiMove.effect_critical_hit_ratio_user;
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                    const effect_volatile_status_foe = apiMove.effect_volatile_status_foe;
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                    const effect_nonvolatile_status_foe = apiMove.effect_nonvolatile_status_foe;
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                    const effect_attack_foe = apiMove.effect_attack_foe;
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                    const effect_defense_foe = apiMove.effect_defense_foe;
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                    const effect_special_attack_foe = apiMove.effect_special_attack_foe;
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                    const effect_special_defense_foe = apiMove.effect_special_defense_foe;
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                    const effect_speed_foe = apiMove.effect_speed_foe;
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                    const effect_accuracy_user = apiMove.effect_accuracy_user;
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                    const effect_evasiveness_user = apiMove.effect_evasiveness_user;
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                    const effect_accuracy_foe = apiMove.effect_accuracy_foe;
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                    const effect_evasiveness_foe = apiMove.effect_evasiveness_foe;
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                    const recoil_percentage = apiMove.recoil_percentage;
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                    const recovery_percentage = apiMove.recovery_percentage;
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                    const min_hits = apiMove.min_hits;
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                    const max_hits = apiMove.max_hits;
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                    const priority = apiMove.priority;
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                    const entry_hazard = apiMove.entry_hazard;
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                    const target = apiMove.target;
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                    const contact = apiMove.contact;
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                    const double_damage_if_hit = apiMove.double_damage_if_hit;
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                    const double_damage_if_no_item = apiMove.double_damage_if_no_item;
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                    const custom_implementation = apiMove.custom_implementation;
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                    const implemented = apiMove.implemented;
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                    const id = apiMove.id;

                    // Helper function to safely parse int with fallback
                    const safeParseInt = (value: unknown, fallback: number): number => {
                        if (value === null || value === undefined || value === '') return fallback;
                        if (typeof value === 'number') return Number.isNaN(value) ? fallback : value;
                        if (typeof value === 'string') {
                            const parsed = parseInt(value, 10);
                            return Number.isNaN(parsed) ? fallback : parsed;
                        }
                        // For other types (shouldn't happen with API response, but handle gracefully)
                        return fallback;
                    };

                    // Helper function to safely parse float with fallback
                    const safeParseFloat = (value: unknown, fallback: number): number => {
                        if (value === null || value === undefined || value === '') return fallback;
                        if (typeof value === 'number') return Number.isNaN(value) ? fallback : value;
                        if (typeof value === 'string') {
                            const parsed = parseFloat(value);
                            return Number.isNaN(parsed) ? fallback : parsed;
                        }
                        // For other types (shouldn't happen with API response, but handle gracefully)
                        return fallback;
                    };

                    // Helper function to safely parse float or return null
                    const safeParseFloatOrNull = (value: unknown): number | null => {
                        if (value === null || value === undefined || value === '') return null;
                        if (typeof value === 'number') return Number.isNaN(value) ? null : value;
                        if (typeof value === 'string') {
                            const parsed = parseFloat(value);
                            return Number.isNaN(parsed) ? null : parsed;
                        }
                        return null;
                    };

                    // Helper function to convert to null if undefined/null/empty, otherwise return trimmed string
                    const safeTrimOrNull = (value: unknown): string | null => {
                        if (value === null || value === undefined || value === '') return null;
                        if (typeof value === 'string') {
                            const trimmed = value.trim();
                            return trimmed === '' ? null : trimmed;
                        }
                        // For other types (shouldn't happen with API response, but handle gracefully)
                        return null;
                    };

                    // Helper to check if value is truthy (not null/undefined/empty)
                    const isTruthy = (value: unknown): boolean => {
                        return value !== null && value !== undefined && value !== '';
                    };

                    // Helper to safely check boolean values
                    const safeBoolean = (value: unknown): boolean => {
                        return value === 'true' || value === true;
                    };
                    const move: Move = {
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                        id,
                        name: isTruthy(name) ? String(name).trim() : '',
                        identifier: isTruthy(identifier) ? String(identifier).trim() : '',
                        effect: safeTrimOrNull(effect),
                        type,
                        category,
                        power: isTruthy(power) ? safeParseInt(power, 0) : null,
                        accuracy: isTruthy(accuracy) ? safeParseInt(accuracy, 0) : null,
                        one_hit_ko: safeBoolean(one_hit_ko),
                        maxPP: safeParseInt(maxPP, 0),
                        effect_probability: safeParseInt(effect_probability, 0),
                        user_effect_probability: safeParseFloatOrNull(user_effect_probability),
                        target_effect_probability: safeParseFloatOrNull(target_effect_probability),
                        critical_hit_stage: safeParseInt(critical_hit_stage, 0),
                        effect_volatile_status_user: safeTrimOrNull(effect_volatile_status_user),
                        effect_nonvolatile_status_user: safeTrimOrNull(effect_nonvolatile_status_user),
                        effect_attack_user: safeParseInt(effect_attack_user, 0),
                        effect_defense_user: safeParseInt(effect_defense_user, 0),
                        effect_special_attack_user: safeParseInt(effect_special_attack_user, 0),
                        effect_special_defense_user: safeParseInt(effect_special_defense_user, 0),
                        effect_speed_user: safeParseInt(effect_speed_user, 0),
                        effect_critical_hit_ratio_user: safeParseInt(effect_critical_hit_ratio_user, 0),
                        effect_volatile_status_foe: safeTrimOrNull(effect_volatile_status_foe),
                        effect_nonvolatile_status_foe: safeTrimOrNull(effect_nonvolatile_status_foe),
                        effect_attack_foe: safeParseInt(effect_attack_foe, 0),
                        effect_defense_foe: safeParseInt(effect_defense_foe, 0),
                        effect_special_attack_foe: safeParseInt(effect_special_attack_foe, 0),
                        effect_special_defense_foe: safeParseInt(effect_special_defense_foe, 0),
                        effect_speed_foe: safeParseInt(effect_speed_foe, 0),
                        effect_accuracy_user: safeParseInt(effect_accuracy_user, 0),
                        effect_evasiveness_user: safeParseInt(effect_evasiveness_user, 0),
                        effect_accuracy_foe: safeParseInt(effect_accuracy_foe, 0),
                        effect_evasiveness_foe: safeParseInt(effect_evasiveness_foe, 0),
                        recoil_percentage: isTruthy(recoil_percentage)
                            ? safeParseFloat(recoil_percentage, 0)
                            : 0,
                        recovery_percentage: isTruthy(recovery_percentage)
                            ? safeParseFloat(recovery_percentage, 0)
                            : 0,
                        min_hits: safeParseInt(min_hits, 1),
                        max_hits: safeParseInt(max_hits, 1),
                        priority: safeParseInt(priority, 0),
                        entry_hazard: safeTrimOrNull(entry_hazard),
                        target: isTruthy(target) ? String(target) : 'foe1',
                        contact: safeBoolean(contact),
                        double_damage_if_hit: safeBoolean(double_damage_if_hit),
                        double_damage_if_no_item: safeBoolean(double_damage_if_no_item),
                        custom_implementation: safeBoolean(custom_implementation),
                        implemented: safeBoolean(implemented),
                        createdAt: Date.now(), // Not in API response, use current timestamp
                        updatedAt: Date.now(), // Not in API response, use current timestamp
                    };

                    this.addMove(move);
                });
            }
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error(`Error fetching moves from API: ${String(error)}`);
            throw error;
        }
    }

    async loadMovesFromCSV(): Promise<void> {
        const data = await fetch('/data/moves.csv');
        const csvText = await data.text();
        const lines = csvText.split('\n').filter((line) => line.trim() !== '');

        // Skip the header row
        for (let i = 1; i < lines.length; i++) {
            const line = lines[i];
            const columns = line.split(',');

            // Parse the CSV columns into a Move object
            const move: Move = {
                id: parseInt(columns[0]),
                identifier: columns[1],
                name: columns[2],
                effect: columns[3] && columns[3].trim() !== '' ? columns[3] : null,
                type: columns[4] as Type,
                category: columns[5] as MoveCategory,
                power: columns[6] && columns[6].trim() !== '' ? parseInt(columns[6]) : null,
                accuracy:
                    columns[7] && columns[7] !== '-1' && columns[7].trim() !== ''
                        ? parseInt(columns[7])
                        : null,
                one_hit_ko: columns[8] === 'true' || columns[8] === '1',
                maxPP: parseInt(columns[9]),
                effect_probability: parseInt(columns[10]) || 0,
                // CSV may not have these columns, set to null (will fallback to effect_probability in Move.js)
                user_effect_probability: null,
                target_effect_probability: null,
                critical_hit_stage: parseInt(columns[11]) || 0,
                effect_volatile_status_user: columns[12] && columns[12].trim() !== '' ? columns[12] : null,
                effect_nonvolatile_status_user: columns[13] && columns[13].trim() !== '' ? columns[13] : null,
                effect_attack_user: parseInt(columns[14]) || 0,
                effect_defense_user: parseInt(columns[15]) || 0,
                effect_special_attack_user: parseInt(columns[16]) || 0,
                effect_special_defense_user: parseInt(columns[17]) || 0,
                effect_speed_user: parseInt(columns[18]) || 0,
                effect_critical_hit_ratio_user: parseInt(columns[19]) || 0,
                effect_volatile_status_foe: columns[20] && columns[20].trim() !== '' ? columns[20] : null,
                effect_nonvolatile_status_foe: columns[21] && columns[21].trim() !== '' ? columns[21] : null,
                effect_attack_foe: parseInt(columns[22]) || 0,
                effect_defense_foe: parseInt(columns[23]) || 0,
                effect_special_attack_foe: parseInt(columns[24]) || 0,
                effect_special_defense_foe: parseInt(columns[25]) || 0,
                effect_speed_foe: parseInt(columns[26]) || 0,
                recoil_percentage: parseFloat(columns[27]) || 0,
                recovery_percentage: parseFloat(columns[28]) || 0,
                min_hits: parseInt(columns[29]) || 1,
                max_hits: parseInt(columns[30]) || 1,
                priority: parseInt(columns[31]) || 0,
                entry_hazard: columns[32] && columns[32].trim() !== '' ? columns[32] : null,
                target: columns[33] || 'foe1',
                contact: columns[34] === 'true' || columns[34] === '1',
                custom_implementation: columns[35] === 'true' || columns[35] === '1',
                implemented: columns[36] === 'true' || columns[36] === '1',
                double_damage_if_hit: columns[37] === 'true' || columns[37] === '1',
                double_damage_if_no_item: columns[38] === 'true' || columns[38] === '1',
                effect_accuracy_user: parseInt(columns[39]) || 0,
                effect_evasiveness_user: parseInt(columns[40]) || 0,
                effect_accuracy_foe: parseInt(columns[41]) || 0,
                effect_evasiveness_foe: parseInt(columns[42]) || 0,
                createdAt: parseInt(columns[43]) || 0,
                updatedAt: parseInt(columns[44]) || 0,
            };

            this.addMove(move);
        }
    }

    // Helper method to add a move
    addMove(move: Move): void {
        this.store[move.identifier] = move;
    }

    // Helper method to get a move
    getMove(identifier: string): Move | undefined {
        return this.store[identifier];
    }
}
